import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Dialog, Button, TextField, Textarea, useDialog, SelectField, Radio, useToast } from '@workspace/design-system/src/components';
import { PaymentRequestConfirmDialog } from './payment-request-confirm-dialog';
import { PaymentRequestTossSuccessDialog } from './payment-request-toss-success-dialog';
import { PaymentRequestDirectSuccessDialog } from './payment-request-direct-success-dialog';
import { Channel } from '@src/models/channel';
import { ChannelInfoTable } from '../components/channel-info-table';
import { PLAN_OPTIONS } from '../constants';
import { useChannelContact, useChannelDetail, useCurrentPaymentRequest } from '@src/hooks/channel/queries';
import { useCreatePaymentRequest } from '@src/hooks/channel/mutations';
import { formatPhoneNumberInput } from '@src/utils/phone';

interface PaymentRequestFormModalProps {
    channel: Channel;
    mode?: 'apply' | 'extend'; // 적용 또는 연장 모드
    currentEndDate?: string; // 연장 모드일 때 현재 만료일 (YYYY-MM-DD)
    onClose: () => void;
    onSubmit: (formData: PaymentRequestFormData) => Promise<void>;
    onRequestTossPG?: (formData: PaymentRequestFormData) => Promise<void>;
    onRefresh?: () => void;
    onSuccess?: (channel: Channel) => void; // 결제 요청 성공 시 호출되는 콜백
}

export interface PaymentRequestFormData {
    planType: string;
    paymentMethod: 'TOSS_PG' | 'DIRECT' | '';
    durationMonths: string;
    amount: string;
    expiryDate: Date | undefined;
    managerName: string;
    managerPhone: string;
    managerEmail: string;
    note: string;
    receiptFile?: File;
}

export function PaymentRequestFormModal({
    channel,
    mode = 'apply',
    currentEndDate,
    onClose,
    onSubmit,
    onRequestTossPG,
    onRefresh,
    onSuccess
}: PaymentRequestFormModalProps) {
    const { openDialog, closeDialog } = useDialog();
    const { openToast } = useToast();
    const createPaymentRequestMutation = useCreatePaymentRequest();

    // 채널 상세 정보 조회
    const { data: channelDetail } = useChannelDetail(channel.channelId);
    // 고객 정보 조회
    const { data: contactData } = useChannelContact(channel.channelId);
    // 진행 중 결제 요청 조회 (결제 링크 만료일 등)
    const { data: currentPaymentRequest } = useCurrentPaymentRequest(channel.channelId);

    // 실제 사용할 채널 (채널 상세 정보 + 고객 정보 포함)
    const effectiveChannel = useMemo(() => {
        let result = { ...channel };
        if (channelDetail) {
            // channelDetail의 channelName이 있으면 사용, 없으면 원래 channel의 channelName 유지
            result = { 
                ...result, 
                ...channelDetail,
                channelName: channelDetail.channelName || channel.channelName
            };
        }
        if (contactData) {
            // contactData는 고객 정보만 포함하므로, 고객 정보 필드만 선택적으로 병합
            // 채널 정보 필드(channelId, channelName 등)는 덮어쓰지 않음
            result = {
                ...result,
                customerName: contactData.customerName ?? result.customerName,
                businessType: contactData.businessType ?? result.businessType,
                contactName: contactData.contactName ?? result.contactName,
                contactTitle: contactData.contactTitle ?? result.contactTitle,
                contactEmail: contactData.contactEmail ?? result.contactEmail,
                contactPhone: contactData.contactPhone ?? result.contactPhone,
                memo: contactData.memo ?? result.memo,
            };
        }
        return result;
    }, [channel, channelDetail, contactData]);

    // 연장 모드일 때는 채널의 현재 요금제로 초기화
    const [formData, setFormData] = useState<PaymentRequestFormData>({
        planType: '',
        paymentMethod: 'TOSS_PG',
        durationMonths: '',
        amount: '',
        expiryDate: undefined,
        managerName: '',
        managerPhone: '',
        managerEmail: '',
        note: '',
    });

    // effectiveChannel이 준비되면 formData의 planType 초기화 (연장 모드일 때만)
    useEffect(() => {
        if (mode === 'extend' && effectiveChannel.plan && !formData.planType) {
            setFormData(prev => ({
                ...prev,
                planType: effectiveChannel.plan || '',
            }));
        }
    }, [mode, effectiveChannel.plan, formData.planType]);

    // effectiveChannel의 담당자 정보를 결제자 정보로 자동 채우기
    useEffect(() => {
        if (effectiveChannel.contactName || effectiveChannel.contactPhone || effectiveChannel.contactEmail) {
            setFormData(prev => {
                const updates: Partial<PaymentRequestFormData> = {};
                
                // 담당자명 -> 결제자명
                if (!prev.managerName && effectiveChannel.contactName) {
                    updates.managerName = effectiveChannel.contactName;
                }
                
                // 연락처 -> 결제자 연락처 (포맷팅 적용)
                if (!prev.managerPhone && effectiveChannel.contactPhone) {
                    updates.managerPhone = formatPhoneNumberInput(effectiveChannel.contactPhone);
                }
                
                // 이메일 -> 결제자 이메일 (유효성 검사)
                if (!prev.managerEmail && effectiveChannel.contactEmail) {
                    updates.managerEmail = effectiveChannel.contactEmail;
                    // 이메일 유효성 검사
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (effectiveChannel.contactEmail && !emailRegex.test(effectiveChannel.contactEmail)) {
                        setEmailError('올바른 이메일 형식이 아닙니다.');
                    } else {
                        setEmailError('');
                    }
                }
                
                return { ...prev, ...updates };
            });
        }
    }, [effectiveChannel.contactName, effectiveChannel.contactPhone, effectiveChannel.contactEmail]);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // 모드에 따른 레이블
    const isExtendMode = mode === 'extend';
    const durationLabel = isExtendMode ? '연장 기간' : '적용 기간';


    // 금액 포맷팅 (천 단위 쉼표)
    const formatCurrency = useCallback((value: string) => {
        const numbers = value.replace(/[^\d]/g, '');
        if (!numbers) return '';
        const clamped = Math.min(Number(numbers), 2_100_000_000);
        if (Number.isNaN(clamped)) return '';
        return clamped.toLocaleString('ko-KR');
    }, []);

    // 이메일 유효성 검사
    const validateEmail = useCallback((email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    // 연락처 유효성 검사 (숫자 10~11자리)
    const validatePhone = useCallback((phone: string) => {
        const digits = phone.replace(/[^\d]/g, '');
        if (!digits) {
            setPhoneError('연락처를 입력해주세요.');
            return false;
        }
        if (digits.length < 10 || digits.length > 11) {
            setPhoneError('연락처는 숫자 10~11자리여야 합니다.');
            return false;
        }
        setPhoneError('');
        return true;
    }, []);

    // 사용 기간 계산
    const calculatedPeriod = useMemo(() => {
        if (!formData.durationMonths) return { startDate: '', endDate: '' };
        const months = parseInt(formData.durationMonths, 10);
        if (isNaN(months) || months < 1) return { startDate: '', endDate: '' };

        let startDate: Date;

        if (isExtendMode && currentEndDate) {
            // 연장 모드: 현재 만료일부터 시작
            startDate = new Date(currentEndDate);
        } else {
            // 적용 모드: 오늘부터 시작
            startDate = new Date();
        }

        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + months);

        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    }, [formData.durationMonths, isExtendMode, currentEndDate]);

    const handleInputChange = useCallback((field: keyof PaymentRequestFormData, value: string | Date | undefined | File) => {
        // 전화번호 포맷팅
        if (field === 'managerPhone' && typeof value === 'string') {
            value = formatPhoneNumberInput(value);
            validatePhone(value);
        }

        // 금액 포맷팅
        if (field === 'amount' && typeof value === 'string') {
            value = formatCurrency(value);
        }

        // 이메일 유효성 검사
        if (field === 'managerEmail' && typeof value === 'string') {
            if (value && !validateEmail(value)) {
                setEmailError('올바른 이메일 형식이 아닙니다.');
            } else {
                setEmailError('');
            }
        }

        // 적용 기간은 숫자만 입력 가능
        if (field === 'durationMonths' && typeof value === 'string') {
            // 숫자가 아닌 문자 제거
            const numericValue = value.replace(/[^\d]/g, '');
            const num = parseInt(numericValue, 10);
            if (isNaN(num) || numericValue === '') {
                value = '';
            } else {
                const clamped = Math.max(1, Math.min(60, num));
                value = String(clamped);
            }
        }

        // 결제 창구 변경 시 토스 PG로 변경하면 세금계산서 초기화
        if (field === 'paymentMethod' && value === 'TOSS_PG') {
            setFormData(prev => ({
                ...prev,
                [field]: value,
                receiptFile: undefined,
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, [formatCurrency, validateEmail, validatePhone]);

    // 파일 선택 핸들러
    const handleFileClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 파일 타입 검증 (이미지 또는 PDF만 허용)
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            alert('이미지(JPG, PNG, GIF) 또는 PDF 파일만 첨부 가능합니다.');
            e.target.value = '';
            return;
        }

        // 파일 크기 제한 (10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('파일 크기는 10MB를 초과할 수 없습니다.');
            e.target.value = '';
            return;
        }

        handleInputChange('receiptFile', file);
        // 동일 파일 재업로드 가능하도록 input 리셋
        e.target.value = '';
    }, [handleInputChange]);

    const handleSubmit = useCallback(() => {
        
        // 결제 창구 선택 확인
        if (!formData.paymentMethod) {
            alert('결제 창구를 선택해주세요.');
            return;
        }
        

        // TODO 1000원 이상 결제 가능하도록 수정
        if (parseInt(formData.amount.replace(/[^\d]/g, ''), 10) < 1000) {
            alert('결제 금액은 1000원 이상이어야 합니다.');
            return;
        }

        // 토스 PG인 경우
        if (formData.paymentMethod === 'TOSS_PG') {
            // 필수 필드 검증 (세금계산서 제외)
            if (
                !formData.planType ||
                !formData.durationMonths ||
                !formData.amount ||
                !formData.managerName ||
                !formData.managerPhone ||
                !formData.managerEmail
            ) {
                alert('필수 항목을 모두 입력해주세요.');
                return;
            }

            // 이메일 유효성 검증
            if (emailError) {
                alert('올바른 이메일 주소를 입력해주세요.');
                return;
            }

            // 연락처 유효성 검증
            if (!validatePhone(formData.managerPhone) || phoneError) {
                alert('올바른 연락처를 입력해주세요.');
                return;
            }

            // 확인 다이얼로그 표시
            openDialog(
                <PaymentRequestConfirmDialog
                    channelName={effectiveChannel.channelName}
                    paymentMethod="TOSS_PG"
                    onClose={closeDialog}
                    onConfirm={async () => {
                        closeDialog();
                        setIsSubmitting(true);

                        try {
                            // API 호출
                            const response = await createPaymentRequestMutation.mutateAsync({
                                channelId: effectiveChannel.channelId,
                                formData,
                            });

                            // 성공 시 기존 콜백 호출 (필요한 경우)
                            if (onRequestTossPG) {
                                await onRequestTossPG(formData);
                            }

                            onClose();
                            
                            // 성공 모달 표시
                            openDialog(
                                <PaymentRequestTossSuccessDialog
                                    channelName={effectiveChannel.channelName}
                                    mode={mode}
                                    onClose={() => {
                                        closeDialog();
                                        onRefresh?.();
                                        // 결제 요청 성공 시 채널 상세 모달 열기
                                        if (onSuccess) {
                                            onSuccess(effectiveChannel);
                                        }
                                    }}
                                    planType={formData.planType}
                                    expiryDate={
                                        currentPaymentRequest?.paymentCode?.expiresAt ||
                                        response.data?.paymentCode?.expiresAt ||
                                        calculatedPeriod.endDate
                                    }
                                />
                            );
                        } catch (error: any) {
                            // 실패 시 토스트 표시
                            const errorMessage = error?.response?.data?.message || error?.message || '결제 요청에 실패했습니다. 다시 시도해주세요.';
                            openToast({
                                type: 'destructive',
                                message: errorMessage,
                            });
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                />
            );
            return;
        }

        // 직접 결제인 경우
        if (formData.paymentMethod === 'DIRECT') {
            // 필수 필드 검증 (세금계산서 포함)
            if (
                !formData.planType ||
                !formData.durationMonths ||
                !formData.amount ||
                !formData.receiptFile ||
                !formData.managerName ||
                !formData.managerPhone ||
                !formData.managerEmail
            ) {
                alert('필수 항목을 모두 입력해주세요.');
                return;
            }

            // 이메일 유효성 검증
            if (emailError) {
                alert('올바른 이메일 주소를 입력해주세요.');
                return;
            }

            // 연락처 유효성 검증
            if (!validatePhone(formData.managerPhone) || phoneError) {
                alert('올바른 연락처를 입력해주세요.');
                return;
            }

            // 확인 다이얼로그 표시
            openDialog(
                <PaymentRequestConfirmDialog
                    channelName={effectiveChannel.channelName}
                    paymentMethod="DIRECT"
                    onClose={closeDialog}
                    onConfirm={async () => {
                        closeDialog();
                        setIsSubmitting(true);

                        try {
                            // API 호출
                            await createPaymentRequestMutation.mutateAsync({
                                channelId: effectiveChannel.channelId,
                                formData,
                            });

                            // 성공 시 기존 콜백 호출 (필요한 경우)
                            await onSubmit(formData);

                            onClose();

                            // 성공 모달 표시
                            openDialog(
                                <PaymentRequestDirectSuccessDialog
                                    channelName={effectiveChannel.channelName}
                                    planType={formData.planType}
                                    mode={mode}
                                    onClose={() => {
                                        closeDialog();
                                        onRefresh?.();
                                        // 결제 요청 성공 시 채널 상세 모달 열기
                                        if (onSuccess) {
                                            onSuccess(effectiveChannel);
                                        }
                                    }}
                                />
                            );
                        } catch (error: any) {
                            // 실패 시 토스트 표시
                            const errorMessage = error?.response?.data?.message || error?.message || '결제 요청에 실패했습니다. 다시 시도해주세요.';
                            openToast({
                                type: 'destructive',
                                message: errorMessage,
                            });
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                />
            );
        }
    }, [formData, emailError, phoneError, validatePhone, effectiveChannel, openDialog, closeDialog, onSubmit, onClose, onRequestTossPG, onRefresh, openToast, createPaymentRequestMutation, calculatedPeriod]);

    // 필수 필드 검증
    const isFormValid = useMemo(() => {
        // 결제 창구 미선택시
        if (!formData.paymentMethod) return false;

        // 공통 필수 필드
        const commonFieldsValid =
            formData.planType &&
            formData.durationMonths &&
            formData.amount &&
            formData.managerName &&
            formData.managerPhone &&
            formData.managerEmail &&
            !emailError &&
            !phoneError;

        // 메모는 적용/연장 모두 선택 사항
        const noteValid = true;

        // 토스 PG인 경우
        if (formData.paymentMethod === 'TOSS_PG') {
            return commonFieldsValid && noteValid;
        }

        // 직접 결제인 경우 (세금계산서 필수)
        if (formData.paymentMethod === 'DIRECT') {
            return commonFieldsValid && noteValid && !!formData.receiptFile;
        }

        return false;
    }, [formData, emailError, isExtendMode]);

    return (
        <Dialog>
            <Dialog.Content className="max-w-[1200px] max-h-[840px] flex flex-col">
                <Dialog.Header title="결제 요청" type="question" />

                <Dialog.Body>
                    {/* 메인 컨텐츠 */}
                    <div className="flex gap-6 flex-1 min-h-0">
                        {/* 왼쪽: 채널 정보 */}
                        <ChannelInfoTable channel={effectiveChannel} showPlan={true} />

                        {/* 세로 구분선 */}
                        <div className="flex items-center justify-center w-px h-[568px]">
                            <div className="bg-[#F0F0F0] h-full w-px" />
                        </div>

                        {/* 오른쪽: 결제 정보 */}
                        <div className="flex-1 flex flex-col gap-6">
                            {/* 요금제 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">요금제</label>
                                </div>
                                <div className="flex-1">
                                    <SelectField
                                        items={PLAN_OPTIONS.filter((item) => {
                                            // 무료, 프로, 리얼월드 제외
                                            return item.value !== 'free' && item.value !== 'pro' && item.value !== 'realworld';
                                        })}
                                        value={formData.planType}
                                        onSelect={(value) => handleInputChange('planType', value as string)}
                                        placeholder="이 채널에 적용하려는 요금제를 선택해 주세요"
                                        readonly={true}
                                        disabled={isExtendMode}
                                    />
                                </div>
                            </div>

                            {/* 결제 창구 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">결제 창구</label>
                                </div>
                                <div className="flex-1 flex gap-4">
                                    <Radio
                                        size="small"
                                        label="토스 PG (카드 결제)"
                                        name="paymentMethod"
                                        value="TOSS_PG"
                                        checked={formData.paymentMethod === 'TOSS_PG'}
                                        onChange={() => handleInputChange('paymentMethod', 'TOSS_PG')}
                                    />
                                    <Radio
                                        size="small"
                                        label="직접 결제 (계좌이체 및 세금계산서 발급)"
                                        name="paymentMethod"
                                        value="DIRECT"
                                        checked={formData.paymentMethod === 'DIRECT'}
                                        onChange={() => handleInputChange('paymentMethod', 'DIRECT')}
                                    />
                                </div>
                            </div>

                            {/* 적용 기간 / 연장 기간 */}
                            <div className="flex gap-[10px] items-start h-10">
                                <div className="flex gap-6 w-[200px] items-start">
                                    <div className="pt-[10px] w-[88px]">
                                        <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">{durationLabel}</label>
                                    </div>
                                    <div className="flex-1">
                                        <TextField
                                            placeholder="12"
                                            value={formData.durationMonths}
                                            onChange={(value) => handleInputChange('durationMonths', value)}
                                        />
                                    </div>
                                </div>
                                <div className="pt-[10px] flex items-center gap-2">
                                    <span className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">개월</span>
                                    {calculatedPeriod.endDate && (
                                        <span className="text-[14px] text-[#3C78F4] leading-[20px] tracking-[-0.1px]">
                                            (사용 기간: {isExtendMode ? `${calculatedPeriod.startDate}부터` : '오늘부터'} {calculatedPeriod.endDate} 까지)
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 결제 금액 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">결제 금액</label>
                                </div>
                                <div className="flex-1 flex items-center gap-2">
                                    <div className="flex-1">
                                        <TextField
                                            placeholder="300,000"
                                            value={formData.amount}
                                            onChange={(value) => handleInputChange('amount', value)}
                                        />
                                    </div>
                                    <span className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">원</span>
                                </div>
                            </div>


                            {/* 결제자명 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">결제자명</label>
                                </div>
                                <div className="flex-1">
                                    <TextField
                                        placeholder="홍길동"
                                        value={formData.managerName}
                                        onChange={(value) => handleInputChange('managerName', value)}
                                    />
                                </div>
                            </div>

                            {/* 결제자 연락처 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">결제자 연락처</label>
                                </div>
                                <div className="flex-1 flex flex-col gap-1">
                                    <TextField
                                        placeholder="010-0000-0000"
                                        value={formData.managerPhone}
                                        onChange={(value) => handleInputChange('managerPhone', value)}
                                    />
                                    {phoneError && (
                                        <span className="text-[12px] text-[#D15445] leading-[16px]">
                                            {phoneError}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 결제자 이메일 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">결제자 이메일</label>
                                </div>
                                <div className="flex-1 flex flex-col gap-1">
                                    <TextField
                                        placeholder="abc@google.com"
                                        value={formData.managerEmail}
                                        onChange={(value) => handleInputChange('managerEmail', value)}
                                    />
                                    {emailError && (
                                        <span className="text-[12px] text-[#D15445] leading-[16px]">
                                            {emailError}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 세금계산서 (직접 결제인 경우에만 표시) */}
                            {formData.paymentMethod === 'DIRECT' && (
                                <div className="flex gap-6 items-start">
                                    <div className="pt-[10px] w-[88px]">
                                        <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">세금계산서</label>
                                    </div>
                                    <div className="flex-1 flex items-center gap-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        {!formData.receiptFile ? (
                                            <Button
                                                styleType="basic"
                                                styleSize="S-32"
                                                onClick={handleFileClick}
                                            >
                                                파일 업로드하기
                                            </Button>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 px-3 py-2 bg-[#F5F5F5] rounded">
                                                    <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                                        {formData.receiptFile.name}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleInputChange('receiptFile', undefined);
                                                            if (fileInputRef.current) {
                                                                fileInputRef.current.value = '';
                                                            }
                                                        }}
                                                        className="w-5 h-5 flex items-center justify-center hover:bg-[#E0E0E0] rounded transition-colors"
                                                        title="파일 삭제"
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M9 3L3 9M3 3L9 9" stroke="#595959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 비고 */}
                            <div className="flex gap-6 items-start">
                                <div className="pt-[10px] w-[88px]">
                                    <label className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">
                                        메모(선택)
                                    </label>
                                </div>
                                <div className="flex-1">
                                    <Textarea
                                        placeholder={isExtendMode ? "연장 사유를 입력해 주세요." : "특이사항을 입력해 주세요."}
                                        value={formData.note}
                                        onChange={(e) => handleInputChange('note', e.target.value)}
                                        rows={3}
                                        className="h-[120px] max-h-[120px] overflow-y-auto w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Body>

                <Dialog.Footer className="border-t border-[#F0F0F0] h-[72px]">
                    <Button
                        styleType="outline-basic"
                        styleSize="M-40"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        닫기
                    </Button>
                    <Button
                        styleType="primary"
                        styleSize="M-40"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting || createPaymentRequestMutation.isPending}
                        isLoading={isSubmitting || createPaymentRequestMutation.isPending}
                    >
                        결제 요청
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}


